import { DateTime } from 'luxon'
import { Injectable } from '@angular/core'
import { ParamMap } from '@angular/router'
import {
    BookingLaunchContext,
    BookingRouteContext,
    LandingPageLaunchParams,
    VoucherLaunchContext,
} from './landing-page-launch.types'
import { LandingPage } from '../domain/LandingPage'
import { adaptStringToDate } from '../../utilities/helper'
import { Time } from '../../domain/Time'

@Injectable({
    providedIn: 'root',
})
export class LandingPageLaunchQueryService {

    fromQueryParamMap(queryParamMap: ParamMap, timeZone: string): LandingPageLaunchParams {
        const startSeconds = this.makeNumber(queryParamMap.get('startSeconds'))
        const startSecondsDate = startSeconds !== undefined
            ? DateTime.fromSeconds(startSeconds, { zone: timeZone }).toJSDate()
            : undefined
        const date = startSecondsDate ?? this.makeDate(queryParamMap.get('date'))
        const availabilityTag = this.getString(queryParamMap, 'availabilityTag')
        const availabilityTagParams = this.parseAvailabilityTag(availabilityTag)
        const areaIdParams = this.parseRoomId(this.getString(queryParamMap, 'areaId'))
        const restrictedReasonIds = this.combineRestrictedReasonIds(
            this.getCsv(queryParamMap, 'rrids') ?? this.getCsv(queryParamMap, 'restrictedReasonIds'),
            availabilityTagParams.restrictedReasonId ?? areaIdParams.restrictedReasonId
        )
        const rwgToken = this.getString(queryParamMap, 'rwg_token')

        return {
            partySize: this.makeNumber(queryParamMap.get('partySize')),
            date,
            idealTime: startSecondsDate ?? this.makeIdealTime(
                queryParamMap.get('idealTime'),
                date
            ),
            startSeconds,
            restrictedReasonIds,
            eventId: this.getString(queryParamMap, 'eid')
                ?? this.getString(queryParamMap, 'eventId')
                ?? availabilityTagParams.eventId
                ?? areaIdParams.eventId,
            voucherId: this.getString(queryParamMap, 'vid'),
            areaId: availabilityTagParams.areaId ?? areaIdParams.areaId,
            autoOpen: this.makeBoolean(queryParamMap.get('ao')),
            rwgToken,
            availabilityTag,
            utmSource: rwgToken ? 'rwg' : this.getString(queryParamMap, 'utm_source'),
            utmMedium: rwgToken ? 'integration' : this.getString(queryParamMap, 'utm_medium'),
            utmCampaign: rwgToken ?? this.getString(queryParamMap, 'utm_campaign'),
            requiresWheelchairAccess: this.makeBoolean(
                queryParamMap.get('requiresWheelchairAccess')
            ),
            requiresDogFriendly: this.makeBoolean(queryParamMap.get('requiresDogFriendly')),
            requiresPramAccessible: this.makeBoolean(
                queryParamMap.get('requiresPramAccessible')
            ),
            requiresHighchairFriendly: this.makeBoolean(
                queryParamMap.get('requiresHighchairFriendly')
            ),
            setupCancelled: queryParamMap.get('setupCancelled') === 'true',
            paymentIntentId: this.getString(queryParamMap, 'paymentIntentId'),
            setupIntentId: this.getString(queryParamMap, 'setupIntentId'),
            setupBookingRequestId: this.getString(
                queryParamMap,
                'setupBookingRequestId'
            ),
        }
    }

    shouldOpenBookingOnLoad(params: LandingPageLaunchParams): boolean {
        return params.autoOpen ?? (!!params.partySize && !!params.date && !!params.idealTime)
    }

    parseBookingPath(bookingPath: string): BookingRouteContext | null {
        const match = bookingPath.match(/^\/?organisation\/([^/]+)\/business\/([^/]+)\/venue\/([^/]+)/)
        if (!match) {
            return null
        }
        return {
            organisationId: match[1],
            businessId: match[2],
            venueId: match[3],
        }
    }

    makeBookingContext(
        landingPage: LandingPage,
        params: LandingPageLaunchParams
    ): BookingLaunchContext | null {
        const routeContext = this.parseBookingPath(landingPage.bookingPath)
        if (!routeContext) {
            return null
        }
        return {
            ...routeContext,
            partySize: params.partySize,
            date: params.date,
            idealTime: params.idealTime,
            restrictedReasonIds: params.restrictedReasonIds,
            eventId: params.eventId,
            areaId: params.areaId,
            utmSource: params.utmSource,
            utmMedium: params.utmMedium,
            utmCampaign: params.utmCampaign,
        }
    }

    makeVoucherContext(
        landingPage: LandingPage,
        voucherId: string
    ): VoucherLaunchContext | null {
        const routeContext = this.parseBookingPath(landingPage.bookingPath)
        if (!routeContext) {
            return null
        }
        return {
            ...routeContext,
            voucherId,
        }
    }

    private parseAvailabilityTag(availabilityTag?: string): {
        eventId?: string
        areaId?: string
        restrictedReasonId?: string
    } {
        if (!availabilityTag) {
            return {}
        }
        const roomIdMatch = availabilityTag.match(/room_id-([^,]+)/)
        const serviceIdMatch = availabilityTag.match(/service_id-([^,]+)/)
        const parsed = this.parseRoomId(roomIdMatch?.[1])
        if (serviceIdMatch && serviceIdMatch[1] !== 'reservation' && !parsed.restrictedReasonId) {
            parsed.restrictedReasonId = serviceIdMatch[1]
        }
        return parsed
    }

    private parseRoomId(roomId?: string): {
        eventId?: string
        areaId?: string
        restrictedReasonId?: string
    } {
        if (!roomId) {
            return {}
        }
        const [entityId, areaId] = roomId.split('-area-')
        if (entityId.startsWith('reason-')) {
            return { restrictedReasonId: entityId.substring('reason-'.length), areaId }
        }
        if (entityId.startsWith('event-')) {
            return { eventId: entityId.substring('event-'.length), areaId }
        }
        return { areaId: roomId }
    }

    private combineRestrictedReasonIds(
        restrictedReasonIds?: string[],
        parsedServiceId?: string
    ): string[] | undefined {
        const combined = [...(restrictedReasonIds ?? [])]
        if (parsedServiceId) {
            combined.push(parsedServiceId)
        }
        return combined.length > 0 ? combined : undefined
    }

    private getString(queryParamMap: ParamMap, key: string): string | undefined {
        return queryParamMap.get(key) ?? undefined
    }

    private getCsv(queryParamMap: ParamMap, key: string): string[] | undefined {
        const value = queryParamMap.get(key)
        if (!value) {
            return undefined
        }
        const values = value.split(',').filter(part => part.length > 0)
        return values.length > 0 ? values : undefined
    }

    private makeNumber(value: string | null): number | undefined {
        if (value === null || value === '') {
            return undefined
        }
        const number = Number(value)
        return Number.isFinite(number) ? number : undefined
    }

    private makeDate(value: string | null): Date | undefined {
        if (!value) {
            return undefined
        }
        const localDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
        if (localDate) {
            return new Date(
                Number(localDate[1]),
                Number(localDate[2]) - 1,
                Number(localDate[3])
            )
        }
        return adaptStringToDate(value)
    }

    private makeIdealTime(value: string | null, date?: Date): Date | undefined {
        if (value && date && /^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
            return new Time(value, null, null).dateTimeOnDate(date)
        }
        return this.makeDate(value)
    }

    private makeBoolean(value: string | null): boolean | undefined {
        if (value === null) {
            return undefined
        }
        return value !== 'false'
    }
}
